import { auth, db } from '../../src/firebase.js';
import { getProduct, getRelatedProducts } from '../productService.js';
import { doc, setDoc, serverTimestamp, collection, addDoc, onSnapshot, getDocs, updateDoc, arrayUnion } from 'firebase/firestore';
import { SHOP_PRODUCTS, CURRENCIES } from '../data/shopData.js';

export class ProductDetail {
    constructor(containerId, productId) {
        this.containerId = containerId;
        this.productId = productId;
        this.el = null;
        this.product = null;
        this.selectedSize = null;
        this.selectedVariantId = null;
        this.activeImageIndex = 0;
        this.selectedQuantity = 1;
        this.showReviewForm = false; // State to control review form visibility
        this.relatedProducts = [];
        this.isTestMode = import.meta.env.VITE_STRIPE_TEST_MODE === 'true';
    }

    async init() {
        this.el = document.getElementById(this.containerId);
        if (!this.el) return;

        this.el.innerHTML = `
            <div class="fg-product-detail fg-product-detail--loading" style="padding: 100px; text-align: center;">
                <p>Loading product details...</p>
            </div>
        `;

        try {
            this.product = await getProduct(this.productId);
            if (!this.product) {
                this.el.innerHTML = '<div style="padding: 100px; text-align: center;"><p>Product not found.</p></div>';
                return;
            }

            this._trackRecentlyViewed();
            // Related products are fetched based on hierarchy
            this.relatedProducts = await getRelatedProducts(this.product.category, this.product.parentCategory, this.productId);

            this.render();
        } catch (error) {
            console.error("Error loading product detail:", error);
            this.el.innerHTML = '<div style="padding: 100px; text-align: center;"><p>Error loading product. Please try again.</p></div>';
        }
    }

    render() {
        if (!this.product) return;

        this.el.innerHTML = `
            <div class="fg-product-detail">
                ${this.isTestMode ? `
                    <div style="background: #fff3cd; color: #856404; padding: 12px; text-align: center; font-weight: 600; border-bottom: 1px solid #ffeeba; font-size: 0.9rem; margin-bottom: 1rem; border-radius: 8px;">
                        Test Mode — use card 4242 4242 4242 4242
                    </div>
                ` : ''}
                ${this.renderShopHeader()}
                <div class="fg-product-detail__desktop">${this.renderDesktopLayout()}</div>
                <div class="fg-product-detail__mobile">${this.renderMobileLayout()}</div>
                ${this.renderRelatedProducts()}
                ${this._renderReviewsSection()}
                ${this._renderRecentlyViewed()}
                ${this.renderEditorialSection()}
                ${this.renderAddToBagPopup()}
                <div class="fg-modal-overlay" id="fg-auth-guard-modal"></div>
            </div>
        `;

        this.bindEvents();
        this.updateActiveImage(this.activeImageIndex);
    }

    renderShopHeader() {
        return `
            <div class="fg-pd-shop-header">
                <h2 class="fg-pd-shop-title">
                    <span class="fg-brand-football">football</span><span class="fg-brand-girl">girl</span> <span class="fg-shop-title-shop">Shop</span>
                </h2>
            </div>
        `;
    }

    renderDesktopLayout() {
        const p = this.product;
        const thumbnails = (p.images || []).map((img, i) => `
            <div class="fg-pd-thumbnail ${i === this.activeImageIndex ? 'is-active' : ''}" data-index="${i}" style="background-image: url('${img}'); background-size: cover; background-position: center; background-color: ${p.imagePlaceholder};"></div>
        `).join('');
        const colors = (p.colors || []).map(color => `<div class="fg-pd-color-dot" style="background-color: ${color};"></div>`).join('');
        
        const sizes = (p.sizes || []).map(size => {
            const isSoldOut = !p.stock || p.stock[size] === 0;
            return `
                <button class="fg-pd-size-btn ${isSoldOut ? 'fg-pd-size-btn--sold-out' : ''}" 
                        data-size="${size}" 
                        ${isSoldOut ? 'disabled' : ''}>
                    ${size}
                    ${isSoldOut ? '<span class="fg-pd-size-btn__status">Sold Out</span>' : ''}
                </button>
            `;
        }).join('');
        const details = (p.details || []).map(detail => `<li>${detail}</li>`).join('');
        const isFullySoldOut = (p.sizes || []).every(size => !p.stock || p.stock[size] === 0);

        return `
            <div class="fg-pd-desktop-inner">
                <div class="fg-pd-gallery-col">
                    <button class="fg-pd-back-btn">← Back to Shop</button>
                    <div class="fg-pd-gallery">
                        <div class="fg-pd-thumbnails">${thumbnails}</div>
                        <div style="flex: 1; overflow: hidden; position: relative; border-radius: 12px;" id="fg-pd-desktop-carousel-container">
                            <div class="fg-pd-carousel-track" id="fg-pd-carousel-track" style="display: flex; transition: transform 0.4s ease; height: 100%;">
                                ${(p.images || []).map(img => `
                                    <div class="fg-pd-carousel-image" style="flex: 0 0 100%; height: 100%; background-image: url('${img}'); background-size: cover; background-position: center; background-color: ${p.imagePlaceholder};"></div>
                                `).join('')}
                            </div>
                            <button class="fg-pd-carousel-arrow fg-pd-carousel-arrow--prev" data-direction="-1" aria-label="Previous image">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"></polyline></svg>
                            </button>
                            <button class="fg-pd-carousel-arrow fg-pd-carousel-arrow--next" data-direction="1" aria-label="Next image">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
                            </button>
                        </div>
                    </div>
                    <div class="fg-pd-dots" style="margin-top: 12px; justify-content: center;">
                        ${(p.images || []).map((_, i) => `<div class="fg-pd-dot ${i === 0 ? 'is-active' : ''}" data-index="${i}"></div>`).join('')}
                    </div>
                </div>
                <div class="fg-pd-info">
                    ${this._renderBreadcrumb()}
                    ${p.isRecycled ? '<p class="fg-pd-recycled">Recycled Materials</p>' : ''}
                    <h1 class="fg-pd-name">${p.name}</h1>
                    <p class="fg-pd-subtitle">${p.subtitle}</p>
                    <p class="fg-pd-price">${p.price}</p>
                    <div class="fg-pd-colors">${colors}</div>
                    <div class="fg-pd-size-header">
                        <label>Select Size</label>
                    </div>
                    <div class="fg-pd-size-grid">${sizes}</div>
                    
                    <div class="fg-pd-qty-wrapper" style="width: 100%; margin-bottom: 1.5rem;">
                        <div class="fg-pd-qty-selector" style="display: flex; align-items: center; justify-content: space-between; width: 100%; border: 1px solid var(--border-color); border-radius: 99px; padding: 6px;">
                            <button class="fg-pd-qty-btn" data-action="dec" ${!this.selectedSize ? 'disabled' : ''} style="background: var(--bg-secondary); border: none; width: 44px; height: 44px; border-radius: 50%; cursor: pointer; font-size: 1.25rem; font-weight: 600; color: var(--text-primary); display: flex; align-items: center; justify-content: center;">−</button>
                            <div style="display: flex; flex-direction: column; align-items: center;">
                                <span style="font-size: 0.75rem; font-weight: 700; text-transform: uppercase; color: var(--text-secondary); margin-bottom: -4px;">Quantity</span>
                                <input type="number" class="fg-pd-qty-input" value="${this.selectedQuantity}" min="1" readonly style="border: none; background: transparent; text-align: center; width: 60px; font-weight: 700; font-size: 1.1rem; color: var(--text-primary); outline: none;">
                            </div>
                            <button class="fg-pd-qty-btn" data-action="inc" ${!this.selectedSize ? 'disabled' : ''} style="background: var(--bg-secondary); border: none; width: 44px; height: 44px; border-radius: 50%; cursor: pointer; font-size: 1.25rem; font-weight: 600; color: var(--text-primary); display: flex; align-items: center; justify-content: center;">+</button>
                        </div>
                        <span class="fg-pd-stock-indicator"></span>
                    </div>

                    <p class="fg-pd-size-warning" style="display: none; color: red; font-size: 0.9rem; margin-top: 0.5rem;">Please select a size</p>
                    ${isFullySoldOut 
                        ? '<button class="fg-pd-notify-me" id="fg-pd-notify-btn">Notify Me</button>' 
                        : `
                            <button class="fg-pd-add-to-bag">Add to Bag</button>
                            <button class="fg-pd-buy-now">Buy Now</button>
                            <p style="text-align: center; font-size: 0.75rem; color: var(--text-secondary); margin-top: 8px;">
                                <a href="#page=returns" style="color: inherit; text-decoration: underline;">Returns & Refund Policy</a>
                            </p>
                        `
                    }
                    <div class="fg-pd-secondary-actions">
                        <button class="fg-pd-favourite">
                            Favourite
                            <svg class="heart-icon" viewBox="0 0 24 24" width="24" height="24"><path fill="none" stroke="currentColor" stroke-width="2" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                        </button>
                        <button class="fg-pd-share" aria-label="Share product">
                            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
                        </button>
                    </div>
                    <div class="fg-pd-pickup">
                        <p>Free Pick-up <a href="#">Find a Store</a></p>
                        <p class="fg-pd-pickup-sub">Available for click and collect at checkout</p>
                    </div>
                    ${this._renderDescriptionSection()}
                    <ul>${details}</ul>
                    
                    <div class="fg-pd-accordions">
                        <div class="fg-shop-accordion">
                            <button class="fg-shop-accordion__toggle" data-accordion-toggle><span>Delivery & Returns</span><span class="fg-shop-accordion__icon">+</span></button>
                            <div class="fg-shop-accordion__content"><p>Delivery & Returns details go here.</p></div>
                        </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    renderMobileLayout() {
        const p = this.product;
        const dots = (p.images || []).map((img, i) => `<div class="fg-pd-dot ${i === this.activeImageIndex ? 'is-active' : ''}" data-index="${i}"></div>`).join('');
        const thumbnails = (p.images || []).map((img, i) => `<div class="fg-pd-thumbnail ${i === this.activeImageIndex ? 'is-active' : ''}" data-index="${i}" style="background-image: url('${img}'); background-size: cover; background-position: center; background-color: ${p.imagePlaceholder};"></div>`).join('');
        const colors = (p.colors || []).map(color => `<div class="fg-pd-color-dot" style="background-color: ${color};"></div>`).join('');

        const sizes = (p.sizes || []).map(size => {
            const isSoldOut = !p.stock || p.stock[size] === 0;
            return `
                <button class="fg-pd-size-btn ${isSoldOut ? 'fg-pd-size-btn--sold-out' : ''}" 
                        data-size="${size}" 
                        ${isSoldOut ? 'disabled' : ''}>
                    ${size}
                    ${isSoldOut ? '<span class="fg-pd-size-btn__status">Sold Out</span>' : ''}
                </button>
            `;
        }).join('');
        const details = (p.details || []).map(detail => `<li>${detail}</li>`).join('');
        const isFullySoldOut = (p.sizes || []).every(size => !p.stock || p.stock[size] === 0);

        return `
            <button class="fg-pd-back-btn">← Back to Shop</button>
            ${p.isRecycled ? '<p class="fg-pd-recycled">Recycled Materials</p>' : ''}
            <h1 class="fg-pd-name">${p.name}</h1>
            <p class="fg-pd-subtitle">${p.subtitle}</p>
            <p class="fg-pd-price">${p.price}</p>
            <div class="fg-pd-mobile-image-wrapper" id="fg-pd-mobile-carousel-container">
                <div class="fg-pd-carousel-track" id="fg-pd-mobile-carousel-track" style="display: flex; transition: transform 0.4s ease; height: 100%;">
                    ${(p.images || []).map(img => `
                        <div class="fg-pd-carousel-image" style="flex: 0 0 100%; height: 100%; background-image: url('${img}'); background-size: cover; background-position: center; background-color: ${p.imagePlaceholder};"></div>
                    `).join('')}
                </div>
                <div class="fg-pd-dots">${dots}</div>
            </div>
            <div class="fg-pd-thumb-strip">${thumbnails}</div>
            <div class="fg-pd-colors">${colors}</div>
            <div class="fg-pd-size-header">
                <label>Select Size</label>
            </div>
            <div class="fg-pd-size-grid">${sizes}</div>

            <div class="fg-pd-qty-wrapper" style="width: 100%; margin-bottom: 1.5rem;">
                <div class="fg-pd-qty-selector" style="display: flex; align-items: center; justify-content: space-between; width: 100%; border: 1px solid var(--border-color); border-radius: 99px; padding: 6px;">
                    <button class="fg-pd-qty-btn" data-action="dec" ${!this.selectedSize ? 'disabled' : ''} style="background: var(--bg-secondary); border: none; width: 44px; height: 44px; border-radius: 50%; cursor: pointer; font-size: 1.25rem; font-weight: 600; color: var(--text-primary); display: flex; align-items: center; justify-content: center;">−</button>
                    <div style="display: flex; flex-direction: column; align-items: center;">
                        <span style="font-size: 0.75rem; font-weight: 700; text-transform: uppercase; color: var(--text-secondary); margin-bottom: -4px;">Quantity</span>
                        <input type="number" class="fg-pd-qty-input" value="${this.selectedQuantity}" min="1" readonly style="border: none; background: transparent; text-align: center; width: 60px; font-weight: 700; font-size: 1.1rem; color: var(--text-primary); outline: none;">
                    </div>
                    <button class="fg-pd-qty-btn" data-action="inc" ${!this.selectedSize ? 'disabled' : ''} style="background: var(--bg-secondary); border: none; width: 44px; height: 44px; border-radius: 50%; cursor: pointer; font-size: 1.25rem; font-weight: 600; color: var(--text-primary); display: flex; align-items: center; justify-content: center;">+</button>
                </div>
                <span class="fg-pd-stock-indicator"></span>
            </div>

            <p class="fg-pd-size-warning" style="display: none; color: red; font-size: 0.9rem; margin-top: 0.5rem;">Please select a size</p>
            ${isFullySoldOut 
                ? '<button class="fg-pd-notify-me" id="fg-pd-notify-btn">Notify Me</button>' 
                : `
                    <button class="fg-pd-add-to-bag">Add to Bag</button>
                    <button class="fg-pd-buy-now">Buy Now</button>
                    <p style="text-align: center; font-size: 0.75rem; color: var(--text-secondary); margin-top: 8px;">
                        <a href="#page=returns" style="color: inherit; text-decoration: underline;">Returns & Refund Policy</a>
                    </p>
                `
            }
            <div class="fg-pd-secondary-actions">
                <button class="fg-pd-favourite">
                    Favourite
                    <svg class="heart-icon" viewBox="0 0 24 24" width="24" height="24"><path fill="none" stroke="currentColor" stroke-width="2" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                </button>
                <button class="fg-pd-share" aria-label="Share product">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
                </button>
            </div>
            <div class="fg-pd-pickup">
                <p>Free Pick-up <a href="#">Find a Store</a></p>
                <p class="fg-pd-pickup-sub">Available for click and collect at checkout</p>
            </div>
            ${this._renderDescriptionSection()}
            <ul>${details}</ul>
            
            <div class="fg-pd-accordions">
                <div class="fg-shop-accordion">
                    <button class="fg-shop-accordion__toggle" data-accordion-toggle><span>Delivery & Returns</span><span class="fg-shop-accordion__icon">+</span></button>
                    <div class="fg-shop-accordion__content"><p>Delivery & Returns details go here.</p></div>
                </div>
            </div>
        `;
    }

    _renderDescriptionSection() {
        const p = this.product;
        if (!p.description) return '';

        // Strip HTML tags temporarily to accurately count sentences for the preview
        const textOnly = p.description.replace(/<[^>]*>?/gm, '');
        const sentences = textOnly.match(/[^.!?]+[.!?]+/g) || [textOnly];
        const hasMore = sentences.length > 2;
        const previewText = hasMore ? sentences.slice(0, 2).join(' ') : p.description;

        if (!hasMore) {
            return `<p class="fg-pd-description">${p.description}</p>`;
        }

        return `
            <div class="fg-pd-description-container">
                <p class="fg-pd-description-preview">${previewText}</p>
                <div class="fg-pd-description-full">
                    ${p.description}
                </div>
                <button class="fg-pd-description-toggle">See full details ↓</button>
            </div>
        `;
    }

    _renderBreadcrumb() {
        const p = this.product;
        return `
            <nav class="fg-breadcrumb" aria-label="Breadcrumb">
                <a href="#" class="fg-breadcrumb__link" data-route="home">Home</a>
                <span class="fg-breadcrumb__separator">/</span>
                <a href="#" class="fg-breadcrumb__link" data-route="shop">Shop</a>
                <span class="fg-breadcrumb__separator">/</span>
                <a href="#" class="fg-breadcrumb__link" data-route="category" data-category="${p.category}">${p.category}</a>
                <span class="fg-breadcrumb__separator">/</span>
                <span class="fg-breadcrumb__current">${p.name}</span>
            </nav>
        `;
    }

    _renderStars(rating) {
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        let starsHtml = '<span class="fg-stars" aria-hidden="true">';
        
        for (let i = 1; i <= 5; i++) {
            if (i <= fullStars) {
                starsHtml += '<span class="fg-star fg-star--full">★</span>';
            } else if (i === fullStars + 1 && hasHalfStar) {
                starsHtml += '<span class="fg-star fg-star--half">★</span>';
            } else {
                starsHtml += '<span class="fg-star fg-star--empty">☆</span>';
            }
        }
        starsHtml += '</span>';
        return starsHtml;
    }

    _renderReviewsSection() {
        const reviews = this.product.reviews || [];
        const reviewCount = reviews.length;
        const user = auth.currentUser;
        const hasReviewed = user && reviews.some(r => r.uid === user.uid);

        let averageRating = 0;
        if (reviewCount > 0) {
            const totalRating = reviews.reduce((sum, r) => sum + (r.rating || 0), 0);
            averageRating = (totalRating / reviewCount);
        }

        let reviewFormOrButtonHtml = '';
        if (user) {
            if (hasReviewed) {
                reviewFormOrButtonHtml = `<p class="fg-reviews-info" style="text-align:center; margin-bottom:2rem;">You have already reviewed this product.</p>`;
            } else if (this.showReviewForm) {
                reviewFormOrButtonHtml = this._renderReviewForm();
            } else {
                reviewFormOrButtonHtml = `
                    <div class="fg-reviews__actions" style="text-align:center; margin-bottom:2rem;">
                        <button class="fg-btn fg-btn--primary" id="fg-pd-write-review" style="width:auto; padding:0.75rem 2rem;">Write a Review</button>
                    </div>
                `;
            }
        } else {
            reviewFormOrButtonHtml = `
                <p class="fg-reviews-info" style="text-align:center; margin-bottom:2rem;">Please <a href="#" id="fg-pd-login-to-review" style="color:var(--primary-blue); font-weight:600;">log in</a> to write a review.</p>
            `;
        }

        return `
            <section class="fg-reviews-section">
                <div class="fg-reviews-section__header">
                    <h2 class="fg-reviews-section__title">Customer Reviews</h2>
                    <div class="fg-reviews-summary">
                        <span class="fg-reviews-summary__average">${averageRating.toFixed(1)}</span>
                        ${this._renderStars(averageRating)}
                        <span class="fg-reviews-summary__count">(${reviewCount} reviews)</span>
                    </div>
                </div>
                ${reviewFormOrButtonHtml}
                <div class="fg-reviews-list">
                    ${this._renderReviewCards()}
                </div>
            </section>
        `;
    }

    _renderReviewForm() {
        return `
            <div class="fg-review-form-container" style="max-width:600px; margin: 0 auto 2rem;">
                <h3 style="margin-bottom:1rem;">Write Your Review</h3>
                <form id="fg-review-form">
                    <div class="fg-star-rating-input" style="margin-bottom:1.5rem;">
                        <input type="radio" id="star5" name="rating" value="5" required /><label for="star5">★</label>
                        <input type="radio" id="star4" name="rating" value="4" /><label for="star4">★</label>
                        <input type="radio" id="star3" name="rating" value="3" /><label for="star3">★</label>
                        <input type="radio" id="star2" name="rating" value="2" /><label for="star2">★</label>
                        <input type="radio" id="star1" name="rating" value="1" /><label for="star1">★</label>
                    </div>
                    <div class="fg-field">
                        <label>Review Title</label>
                        <input type="text" id="review-title" placeholder="Summarize your experience" required>
                    </div>
                    <div class="fg-field">
                        <label>Review Body</label>
                        <textarea id="review-body" placeholder="What did you like or dislike?" required></textarea>
                    </div>
                    <div style="display:flex; gap:12px;">
                        <button type="submit" class="fg-btn fg-btn--primary">Submit Review</button>
                        <button type="button" id="fg-review-form-cancel" class="fg-btn">Cancel</button>
                    </div>
                </form>
            </div>
        `;
    }

    _renderReviewCards() {
        const reviews = this.product.reviews || [];
        if (reviews.length === 0) return '<p class="fg-reviews-empty" style="text-align:center; padding:2rem;">Be the first to review this product!</p>';

        return reviews.map(r => `
            <div class="fg-review-card">
                <div class="fg-review-card__header" style="display:flex; justify-content:space-between; margin-bottom:8px;">
                    <span class="fg-review-card__author" style="font-weight:700;">${r.name}</span>
                    ${this._renderStars(r.rating)}
                </div>
                <h4 class="fg-review-card__title" style="font-weight:700; margin-bottom:8px;">${r.title}</h4>
                <p class="fg-review-card__body">${r.body}</p>
            </div>
        `).join('');
    }

    _trackRecentlyViewed() {
        const key = 'fg_recently_viewed';
        const viewed = JSON.parse(localStorage.getItem(key) || '[]');
        const current = {
            id: this.productId,
            name: this.product.name,
            price: this.product.price,
            image: (this.product.images || [])[0] || ''
        };
        
        const filtered = viewed.filter(item => item.id !== this.productId);
        filtered.unshift(current);
        localStorage.setItem(key, JSON.stringify(filtered.slice(0, 6)));
    }

    _renderRecentlyViewed() {
        const key = 'fg_recently_viewed';
        const viewed = JSON.parse(localStorage.getItem(key) || '[]');
        const others = viewed.filter(item => item.id !== this.productId);
        
        if (others.length === 0) return '';

        const grid = others.map(p => `
            <article class="fg-rv-card" data-product-id="${p.id}">
                <div class="fg-rv-card__img">
                    ${p.image ? `<img src="${p.image}" alt="${p.name}" style="width:100%; height:100%; object-fit:cover; border-radius:8px;">` : ''}
                </div>
                <div class="fg-rv-card__body">
                    <h4 class="fg-rv-card__name">${p.name}</h4>
                    <p class="fg-rv-card__price">${p.price}</p>
                </div>
            </article>
        `).join('');

        return `
            <section class="fg-recently-viewed">
                <div class="fg-recently-viewed__inner">
                    <div class="fg-recently-viewed__header">
                        <h2 class="fg-recently-viewed__title">Recently Viewed</h2>
                    </div>
                    <div class="fg-rv-grid">${grid}</div>
                </div>
            </section>
        `;
    }

    async _handleShare() {
        const shareData = {
            title: this.product.name,
            text: this.product.subtitle,
            url: window.location.href
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                // User cancelled or share failed silently
            }
        } else {
            try {
                await navigator.clipboard.writeText(shareData.url);
                this._showToast('Link copied to clipboard');
            } catch (err) {
                console.error('Clipboard copy failed', err);
            }
        }
    }

    _showToast(msg) {
        const toast = document.createElement('div');
        toast.className = 'fg-toast';
        toast.textContent = msg;
        document.body.appendChild(toast);
        requestAnimationFrame(() => toast.classList.add('fg-toast--visible'));
        setTimeout(() => {
            toast.classList.remove('fg-toast--visible');
            toast.addEventListener('transitionend', () => toast.remove());
        }, 2500);
    }

    _requireAuth(actionName) {
        if (auth.currentUser) return true;

        const modal = this.el.querySelector('#fg-auth-guard-modal');
        modal.innerHTML = `
            <div class="fg-modal fg-auth-modal">
                <img src="/images/logo2.png" class="fg-auth-modal__logo" alt="FootballGirl">
                <h2 class="fg-auth-modal__title">Sign in Required</h2>
                <p class="fg-auth-modal__text">Please log in or create an account to ${actionName}.</p>
                <div class="fg-auth-modal__actions">
                    <button class="fg-btn fg-btn--primary" id="fg-guard-login">Sign In / Sign Up</button>
                    <button class="fg-btn" id="fg-guard-close">Maybe Later</button>
                </div>
            </div>
        `;
        modal.classList.add('is-open');
        return false;
    }

    renderAddToBagPopup() {
        return `
            <div class="fg-atb-popup" id="fg-atb-popup" style="display:none;">
                <div class="fg-atb-popup__inner">
                    <div class="fg-atb-popup__header">
                        <span class="fg-atb-popup__check">✓</span>
                        <span class="fg-atb-popup__title">Added to Bag</span>
                        <button class="fg-atb-popup__close" id="fg-atb-popup-close">✕</button>
                    </div>
                    <div class="fg-atb-popup__product">
                        <div class="fg-atb-popup__img" id="fg-atb-popup-img"></div>
                        <div class="fg-atb-popup__info">
                            <p class="fg-atb-popup__name" id="fg-atb-popup-name"></p>
                            <p class="fg-atb-popup__subtitle" id="fg-atb-popup-subtitle"></p>
                            <p class="fg-atb-popup__size" id="fg-atb-popup-size"></p>
                            <p class="fg-atb-popup__price" id="fg-atb-popup-price"></p>
                        </div>
                    </div>
                    <div class="fg-atb-popup__actions">
                        <button class="fg-atb-popup__view-bag" id="fg-atb-view-bag">View Bag</button>
                        <button class="fg-atb-popup__checkout" id="fg-atb-checkout">Checkout</button>
                    </div>
                </div>
            </div>
            <div class="fg-atb-popup__overlay" id="fg-atb-overlay" style="display:none;"></div>
        `;
    }

    /**
     * Initiates the Stripe checkout process via Firestore.
     */
    async _initiateCheckout() {
        console.log("[Checkout Flow] Step 1: Auth check...");
        const user = auth.currentUser;
        if (!user) {
            console.warn("[Checkout Flow] Auth failed: No user.");
            window.dispatchEvent(new CustomEvent('fg:navigate', {
                detail: { page: 'login' }
            }));
            return;
        }
        console.log("[Checkout Flow] Auth success:", user.uid);

        if (!this.selectedSize) {
            this.el.querySelectorAll('.fg-pd-size-warning').forEach(w => w.style.display = 'block');
            return;
        }

        const userId = user.uid;
        const sessionsRef = collection(db, 'customers', userId, 'checkout_sessions');

        const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";
        const isTestKey = stripeKey.startsWith('pk_test');
        console.log(`[Checkout Flow] Stripe Key: ${isTestKey ? 'TEST' : 'LIVE'} (${stripeKey.substring(0, 7)}...)`);

        try {
            const price = this.product.stripePriceId || Math.round(this.product.priceValue * 100);

            console.log("[Checkout Flow] Step 2: Creating session document in Firestore...");
            const docRef = await addDoc(sessionsRef, {
                price: price,
                mode: 'payment',
                success_url: window.location.origin + '?success=true',
                cancel_url: window.location.origin + '?cancelled=true',
                metadata: {
                    productId: this.productId,
                    productName: this.product.name,
                    size: this.selectedSize || 'Not Selected',
                    variantId: String(this.selectedVariantId || "")
                }
            });
            console.log("[Checkout Flow] Session document created:", docRef.id);

            // Real-time listener for the extension to populate the checkout URL
            onSnapshot(docRef, (snap) => {
                const data = snap.data();
                if (data?.url) {
                    console.log("[Checkout Flow] Step 3: Redirecting to Stripe...");
                    window.location.assign(data.url);
                }
                if (data?.error) alert(`Stripe Error: ${data.error.message}`);
            });
        } catch (error) {
            console.error("[ProductDetail] Checkout Error:", error);
            alert("Failed to start checkout session. Please try again.");
        }
    }

    async _addToBag() {
        const user = auth.currentUser;
        if (!user) {
            this._requireAuth('add items to bag');
            return;
        }

        if (!this.selectedSize) {
            this.el.querySelectorAll('.fg-pd-size-warning').forEach(w => w.style.display = 'block');
            return;
        }

        try {
            const cartRef = collection(db, 'users', user.uid, 'cart');
            await addDoc(cartRef, {
                productId: this.productId,
                name: this.product.name,
                subtitle: this.product.subtitle,
                price: this.product.price,
                priceValue: this.product.priceValue,
                image: (this.product.images || [])[0] || '',
                size: this.selectedSize,
                variantId: String(this.selectedVariantId || ""),
                quantity: this.selectedQuantity,
                addedAt: serverTimestamp()
            });

            this.showAddToBagPopup();
            
            const snap = await getDocs(cartRef);
            window.dispatchEvent(new CustomEvent('fg:cartupdated', { 
                detail: { count: snap.size } 
            }));
        } catch (error) {
            console.error("[ProductDetail] Add to Bag Error:", error);
            alert("Failed to add item to bag.");
        }
    }

    _updateQuantityUI() {
        const stockIndicators = this.el.querySelectorAll('.fg-pd-stock-indicator');
        const qtyInputs = this.el.querySelectorAll('.fg-pd-qty-input');
        
        qtyInputs.forEach(input => input.value = this.selectedQuantity);

        // Calculate dynamic total price
        const basePrice = parseFloat(this.product.price.replace(/[^0-9.-]+/g, ""));
        const symbol = this.product.price.match(/[£$€]/) ? this.product.price.match(/[£$€]/)[0] : '';
        const totalPrice = (basePrice * this.selectedQuantity).toFixed(2);
        const priceEls = this.el.querySelectorAll('.fg-pd-price');
        priceEls.forEach(el => el.textContent = `${symbol}${totalPrice}`);

        if (this.selectedSize && this.product.stock) {
            const count = this.product.stock[this.selectedSize] || 0;
            stockIndicators.forEach(el => {
                el.style.display = 'inline-block';
                if (count > 0) {
                    el.textContent = `${count} in stock`;
                    el.classList.toggle('low-stock', count <= 5);
                } else {
                    el.textContent = 'Out of stock';
                    el.classList.remove('low-stock');
                }
            });
        } else {
            stockIndicators.forEach(el => el.style.display = 'none');
        }
    }

    showAddToBagPopup(product = this.product, size = this.selectedSize) {
        const popup = this.el.querySelector('#fg-atb-popup');
        const overlay = this.el.querySelector('#fg-atb-overlay');
        if (!popup) return;

        this.el.querySelector('#fg-atb-popup-name').textContent = product.name;
        this.el.querySelector('#fg-atb-popup-subtitle').textContent = product.subtitle;
        this.el.querySelector('#fg-atb-popup-size').textContent = `Size: ${size}`;
        this.el.querySelector('#fg-atb-popup-price').textContent = product.price;
        this.el.querySelector('#fg-atb-popup-img').style.backgroundImage = 'url(' + (product.images || [])[0] + ')';
        this.el.querySelector('#fg-atb-popup-img').style.backgroundSize = 'cover';
        this.el.querySelector('#fg-atb-popup-img').style.backgroundPosition = 'center';

        popup.style.display = 'block';
        overlay.style.display = 'block';

        if (this.popupTimer) clearTimeout(this.popupTimer);
        this.popupTimer = setTimeout(() => this.closeAddToBagPopup(), 5000);
    }

    _showQuickAddPopover(btn, product) {
        const existing = document.querySelector('.fg-quick-add-popover');
        if (existing) existing.remove();

        const popover = document.createElement('div');
        popover.className = 'fg-quick-add-popover';
        
        const sizesHtml = (product.sizes || []).map(size => `
            <button class="fg-quick-add-popover__size-btn" data-size="${size}">${size}</button>
        `).join('');

        popover.innerHTML = `
            <div class="fg-quick-add-popover__header">Select Size</div>
            <div class="fg-quick-add-popover__grid">${sizesHtml}</div>
            <div class="fg-quick-add-popover__footer">
                <button class="fg-quick-add-popover__wishlist-btn">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                    Add to Wishlist
                </button>
            </div>
        `;

        document.body.appendChild(popover);

        const rect = btn.getBoundingClientRect();
        const popoverHeight = popover.offsetHeight;
        const popoverWidth = popover.offsetWidth;
        
        const top = rect.top + window.scrollY - popoverHeight - 10;
        let left = rect.left + window.scrollX + (rect.width / 2) - (popoverWidth / 2);
        
        // Keep within viewport bounds
        const margin = 10;
        left = Math.max(margin, Math.min(left, window.innerWidth - popoverWidth - margin));

        popover.style.top = `${top}px`;
        popover.style.left = `${left}px`;

        requestAnimationFrame(() => popover.classList.add('is-visible'));

        popover.addEventListener('click', (e) => {
            const sizeBtn = e.target.closest('.fg-quick-add-popover__size-btn');
            if (sizeBtn) {
                const size = sizeBtn.dataset.size;
                window.dispatchEvent(new CustomEvent('fg:addtocart', {
                    detail: { product, size }
                }));
                this.showAddToBagPopup(product, size);
                popover.remove();
            }
        });

        const wishlistBtn = popover.querySelector('.fg-quick-add-popover__wishlist-btn');
        const isWishlisted = this._getWishlist().includes(product.id);
        
        if (isWishlisted) {
            wishlistBtn.classList.add('is-active');
            wishlistBtn.querySelector('svg').style.fill = 'currentColor';
        }

        wishlistBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const isActive = this._trackWishlist(product.id);
            wishlistBtn.classList.toggle('is-active', isActive);
            wishlistBtn.querySelector('svg').style.fill = isActive ? 'currentColor' : 'none';
            
            // Sync main favorite button if it's the same product
            if (product.id === this.productId) {
                this._updateFavoriteUI(isActive);
            }
            
            this._showToast(isActive ? 'Added to Wishlist' : 'Removed from Wishlist');
        });

        const closeHandler = (e) => {
            if (!popover.contains(e.target) && e.target !== btn) {
                popover.remove();
                document.removeEventListener('click', closeHandler);
            }
        };
        setTimeout(() => document.addEventListener('click', closeHandler), 0);
    }

    _getWishlist() {
        return JSON.parse(localStorage.getItem('fg_wishlist') || '[]');
    }

    _trackWishlist(productId) {
        let wishlist = this._getWishlist();
        const index = wishlist.indexOf(productId);
        let isActive = false;

        if (index > -1) {
            wishlist.splice(index, 1);
            isActive = false;
        } else {
            wishlist.push(productId);
            isActive = true;
        }

        localStorage.setItem('fg_wishlist', JSON.stringify(wishlist));
        window.dispatchEvent(new CustomEvent('fg:wishlistupdated', { 
            detail: { wishlist, productId, isActive } 
        }));
        return isActive;
    }

    _updateFavoriteUI(isActive) {
        this.el.querySelectorAll('.fg-pd-favourite .heart-icon').forEach(heart => {
            heart.classList.toggle('is-filled', isActive);
            heart.style.fill = isActive ? 'red' : 'none';
            heart.style.stroke = isActive ? 'red' : 'currentColor';
        });
    }

    closeAddToBagPopup() {
        const popup = this.el.querySelector('#fg-atb-popup');
        const overlay = this.el.querySelector('#fg-atb-overlay');
        if (popup) popup.style.display = 'none';
        if (overlay) overlay.style.display = 'none';
        if (this.popupTimer) clearTimeout(this.popupTimer);
    }

    openSizeGuide() {
        const modal = this.el.querySelector('#fg-size-guide-modal');
        if (!modal) return;
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        // Focus Trap: Focus the close button
        modal.querySelector('.fg-size-guide-close').focus();
    }

    closeSizeGuide() {
        const modal = this.el.querySelector('#fg-size-guide-modal');
        if (!modal) return;
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
    }

    renderRelatedProducts() {
        if (!this.relatedProducts || this.relatedProducts.length === 0) return '';

        const cards = this.relatedProducts.map(p => `
            <div class="fg-stylist__card" data-product-id="${p.id}">
                <div class="fg-stylist__card-img" style="background-color: ${p.imagePlaceholder};">
                    ${(p.images || [])[0] ? `<img src="${p.images[0]}" alt="${p.name}" style="width:100%; height:100%; object-fit:cover; border-radius:12px;">` : ''}
                </div>
                <div class="fg-stylist__card-body">
                    <h4 class="fg-stylist__card-label">${p.name}</h4>
                    <p class="fg-stylist__card-price">${p.price}</p>
                </div>
            </div>
        `).join('');

        return `
            <section class="fg-stylist">
                <div class="fg-stylist__header">
                    <h2 class="fg-stylist__title">Style it with</h2>
                    <p class="fg-stylist__brand-tag">
                        <span class="fg-brand-football">football</span><span class="fg-brand-girl">girl</span> stylist
                    </p>
                </div>
                <div class="fg-stylist__carousel-wrapper">
                    <button class="fg-stylist__nav fg-stylist__nav--prev" aria-label="Previous">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                    </button>
                    <div class="fg-stylist__carousel">${cards}</div>
                    <button class="fg-stylist__nav fg-stylist__nav--next" aria-label="Next">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </button>
                </div>
            </section>
        `;
    }

    renderEditorialSection() {
        return `
            <section class="fg-editorial">
                <div class="fg-editorial__image-placeholder" style="min-height: 500px;">
                    <div class="fg-editorial__text-content">
                        <h2>Set the Tone</h2>
                        <p>Designed for the pitch and the streets.</p>
                    </div>
                </div>
                <div class="fg-editorial__image-placeholder" style="min-height: 400px;"></div>
            </section>
        `;
    }

    updateActiveImage(index) {
        this.activeImageIndex = index;
        const tracks = this.el.querySelectorAll('#fg-pd-carousel-track, #fg-pd-mobile-carousel-track');
        
        tracks.forEach(track => {
            if (track) track.style.transform = `translateX(-${index * 100}%)`;
        });

        this.el.querySelectorAll('.fg-pd-thumbnail').forEach((thumb, i) => {
            thumb.classList.toggle('is-active', i === index);
        });
        this.el.querySelectorAll('.fg-pd-dot').forEach((dot, i) => {
            dot.classList.toggle('is-active', i === index);
        });
    }

    bindEvents() {
        const totalImages = (this.product.images || []).length;
    
        // Carousel Swipe Logic
        const containers = this.el.querySelectorAll('#fg-pd-desktop-carousel-container, #fg-pd-mobile-carousel-container');
        containers.forEach(container => {
            let startX = 0;
            container.style.touchAction = 'pan-y';
    
            container.addEventListener('touchstart', (e) => {
                startX = e.touches[0].clientX;
            }, { passive: true });
    
            container.addEventListener('touchend', (e) => {
                const deltaX = e.changedTouches[0].clientX - startX;
                if (deltaX < -50) {
                    this.updateActiveImage((this.activeImageIndex + 1) % totalImages);
                } else if (deltaX > 50) {
                    this.updateActiveImage((this.activeImageIndex - 1 + totalImages) % totalImages);
                }
            }, { passive: true });
        });
    
        // Arrow Clicks
        this.el.querySelectorAll('.fg-pd-carousel-arrow').forEach(arrowBtn => {
            arrowBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const direction = parseInt(arrowBtn.dataset.direction, 10);
                let newIndex = this.activeImageIndex + direction;
                if (newIndex < 0) newIndex = totalImages - 1;
                if (newIndex >= totalImages) newIndex = 0;
                this.updateActiveImage(newIndex);
            });
        });
    
        // Thumbnail and Dot Clicks
        this.el.addEventListener('click', e => {
            const target = e.target.closest('.fg-pd-thumbnail, .fg-pd-dot');
            if (target && target.dataset.index !== undefined) {
                e.preventDefault();
                this.updateActiveImage(parseInt(target.dataset.index, 10));
            }
        });
    
        // Breadcrumb Navigation
        this.el.querySelectorAll('.fg-breadcrumb__link').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const route = link.dataset.route;
                const detail = { page: 'shop' };
                if (route === 'category') detail.category = link.dataset.category;
                window.dispatchEvent(new CustomEvent('fg:navigate', { detail }));
            });
        });
    
        this.el.querySelectorAll('.fg-pd-back-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                window.dispatchEvent(new CustomEvent('fg:navigate', { detail: { page: 'shop' } }));
            });
        });
    
        this.el.querySelectorAll('.fg-pd-size-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.el.querySelectorAll('.fg-pd-size-btn').forEach(b => b.classList.remove('is-selected'));
                btn.classList.add('is-selected');
                this.selectedSize = btn.dataset.size;
                this.selectedVariantId = this.product.variantIds && this.product.variantIds[this.selectedSize] ? this.product.variantIds[this.selectedSize] : null;
                this.selectedQuantity = 1;
                this.el.querySelectorAll('.fg-pd-size-warning').forEach(w => w.style.display = 'none');
                this.el.querySelectorAll('.fg-pd-qty-input').forEach(input => input.value = 1);
                this.el.querySelectorAll('.fg-pd-qty-btn').forEach(b => b.removeAttribute('disabled'));
                this._updateQuantityUI();
            });
        });
    
        this.el.querySelectorAll('.fg-pd-buy-now').forEach(btn => {
            btn.addEventListener('click', () => this._initiateCheckout());
        });
    
        this.el.querySelectorAll('.fg-pd-add-to-bag').forEach(btn => {
            btn.addEventListener('click', () => this._addToBag());
        });
    
        this.el.addEventListener('click', e => {
            if (e.target.id === 'fg-guard-login') window.app.handleNavigation('login');
            if (e.target.id === 'fg-guard-close' || e.target.id === 'fg-auth-guard-modal') {
                this.el.querySelector('#fg-auth-guard-modal').classList.remove('is-open');
            }
        });
    
        // Quantity Selector Logic
        this.el.addEventListener('click', e => {
            const qtyBtn = e.target.closest('.fg-pd-qty-btn');
            if (!qtyBtn || qtyBtn.hasAttribute('disabled')) return;
            const action = qtyBtn.dataset.action;
            const maxStock = (this.product.stock && this.selectedSize) ? (this.product.stock[this.selectedSize] || 0) : 99;
            if (action === 'inc') {
                if (this.selectedQuantity < maxStock) {
                    this.selectedQuantity++;
                } else {
                    this._showToast(`Only ${maxStock} items available in this size`);
                }
            } else if (action === 'dec' && this.selectedQuantity > 1) {
                this.selectedQuantity--;
            }
            this._updateQuantityUI();
        });
    
        // Add to bag popup
        this.el.querySelector('#fg-atb-popup-close')?.addEventListener('click', () => this.closeAddToBagPopup());
        this.el.querySelector('#fg-atb-overlay')?.addEventListener('click', () => this.closeAddToBagPopup());
        this.el.querySelector('#fg-atb-view-bag')?.addEventListener('click', () => {
            this.closeAddToBagPopup();
            if (window.app && window.app.cartDrawer) {
                window.app.cartDrawer.open();
            }
        });
        this.el.querySelector('#fg-atb-checkout')?.addEventListener('click', () => {
            this.closeAddToBagPopup();
            if (window.app && window.app.cartDrawer) {
                window.app.cartDrawer.open();
            }
        });
    
        window.addEventListener('fg:cartupdated', (e) => {
            const count = e.detail.count;
            const countEl = this.el.querySelector('#fg-pd-bag-btn .fg-bag-count');
            if (countEl) {
                countEl.textContent = count; // Update text content
                const isVisible = count > 0;
                countEl.style.display = isVisible ? 'flex' : 'none'; // Show/hide
                if (isVisible) {
                    countEl.classList.remove('fg-bag-count--bounce'); // Reset animation
                    void countEl.offsetWidth; // Trigger reflow
                    countEl.classList.add('fg-bag-count--bounce'); // Add animation class
                }
            }
        });
    
        this.el.querySelector('#fg-pd-notify-btn')?.addEventListener('click', () => {
            if (this._requireAuth('receive restock notifications')) {
                this._showToast('We will notify you when this is back in stock!');
            }
        });
    
        this.el.querySelectorAll('.fg-pd-share').forEach(btn => {
            btn.addEventListener('click', () => this._handleShare());
        });
    
        const currentlyWishlisted = this._getWishlist().includes(this.productId);
        this._updateFavoriteUI(currentlyWishlisted);
    
        this.el.querySelectorAll('.fg-pd-favourite').forEach(btn => {
            btn.addEventListener('click', () => {
                if (this._requireAuth('favourite items')) {
                    const isActive = this._trackWishlist(this.productId);
                    this._updateFavoriteUI(isActive);
                    this._showToast(isActive ? 'Added to Favourites' : 'Removed from Favourites');
                }
            });
        });
    
        this.el.querySelector('#fg-pd-write-review')?.addEventListener('click', () => {
            this.showReviewForm = true;
            this.render();
        });
    
        this.el.querySelector('#fg-pd-login-to-review')?.addEventListener('click', (e) => {
            e.preventDefault();
            window.app.handleNavigation('login');
        });
    
        this.el.querySelector('#fg-review-form')?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const user = auth.currentUser;
            if (!user) { this._requireAuth('submit a review'); return; }
            const rating = parseInt(this.el.querySelector('input[name="rating"]:checked')?.value);
            const title = this.el.querySelector('#review-title').value.trim();
            const body = this.el.querySelector('#review-body').value.trim();
            if (!rating || !title || !body) { alert('Please provide a rating, title, and review body.'); return; }
            const newReview = {
                uid: user.uid,
                name: user.displayName || 'Anonymous',
                rating, title, body,
                date: new Date().toISOString().split('T')[0],
                timestamp: serverTimestamp()
            };
            try {
                const productRef = doc(db, 'products', this.productId);
                await updateDoc(productRef, { reviews: arrayUnion(newReview) });
                if (!this.product.reviews) this.product.reviews = [];
                this.product.reviews.push(newReview);
                this.showReviewForm = false;
                this.render();
                this._showToast('Review submitted successfully!');
            } catch (error) {
                console.error('Error submitting review:', error);
                alert('Failed to submit review. Please try again.');
            }
        });
    
        this.el.querySelector('#fg-review-form-cancel')?.addEventListener('click', () => {
            this.showReviewForm = false;
            this.render();
        });
    
        this.el.querySelectorAll('.fg-pd-description-toggle').forEach(btn => {
            btn.addEventListener('click', () => {
                const container = btn.closest('.fg-pd-description-container');
                const fullDesc = container.querySelector('.fg-pd-description-full');
                const preview = container.querySelector('.fg-pd-description-preview');
                const isExpanding = !fullDesc.classList.contains('is-expanded');
                if (isExpanding) {
                    fullDesc.classList.add('is-expanded');
                    fullDesc.style.maxHeight = `${fullDesc.scrollHeight}px`;
                    btn.textContent = 'Hide details ↑';
                    if (preview) preview.style.display = 'none';
                } else {
                    fullDesc.classList.remove('is-expanded');
                    fullDesc.style.maxHeight = '0px';
                    btn.textContent = 'See full details ↓';
                    if (preview) preview.style.display = 'block';
                }
            });
        });
    
        this.el.querySelectorAll('[data-accordion-toggle]').forEach(button => {
            button.addEventListener('click', () => {
                const content = button.nextElementSibling;
                const icon = button.querySelector('.fg-shop-accordion__icon');
                const isOpen = content.classList.toggle('is-open');
                button.setAttribute('aria-expanded', isOpen);
                icon.textContent = isOpen ? '−' : '+';
            });
        });
    
        const carousel = this.el.querySelector('.fg-stylist__carousel');
        const prevBtn = this.el.querySelector('.fg-stylist__nav--prev');
        const nextBtn = this.el.querySelector('.fg-stylist__nav--next');
    
        if (carousel && prevBtn && nextBtn) {
            const updateArrows = () => {
                prevBtn.style.display = carousel.scrollLeft <= 10 ? 'none' : 'flex';
                nextBtn.style.display = carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 10 ? 'none' : 'flex';
            };
            const scrollOne = (direction) => {
                const card = carousel.querySelector('.fg-stylist__card');
                if (!card) return;
                carousel.scrollBy({ left: direction * (card.offsetWidth + 16), behavior: 'smooth' });
            };
            prevBtn.addEventListener('click', () => scrollOne(-1));
            nextBtn.addEventListener('click', () => scrollOne(1));
            carousel.addEventListener('scroll', updateArrows, { passive: true });
            setTimeout(updateArrows, 100);
        }
    
        this.el.querySelectorAll('.fg-stylist__card').forEach(card => {
            card.addEventListener('click', () => {
                window.dispatchEvent(new CustomEvent('fg:navigate', {
                    detail: { page: 'product-detail', productId: card.dataset.productId }
                }));
            });
        });
    
        this.el.querySelectorAll('.fg-rv-card').forEach(card => {
            card.addEventListener('click', (e) => {
                if (e.target.closest('.fg-rv-card__quick-add')) return;
                window.dispatchEvent(new CustomEvent('fg:navigate', {
                    detail: { page: 'product-detail', productId: card.dataset.productId }
                }));
            });
        });
    
        this.el.querySelectorAll('.fg-rv-card__quick-add').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const card = btn.closest('.fg-rv-card');
                const product = SHOP_PRODUCTS.find(p => p.id === card.dataset.productId);
                if (product && product.sizes && product.sizes.length > 1) {
                    this._showQuickAddPopover(btn, product);
                } else if (product) {
                    const size = (product.sizes && product.sizes.length > 0) ? product.sizes[0] : 'One Size';
                    window.dispatchEvent(new CustomEvent('fg:addtocart', { detail: { product, size } }));
                    this.showAddToBagPopup(product, size);
                }
            });
        });
    }
}